import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, ExternalLink, Users, Music2, Info, Play, Pause } from 'lucide-react';
import { spotifyArtistSearch, spotifyArtistDetail } from '../lib/api';

/** Live Spotify artist lookup — embedded inside the Spotify agency card.
 * Degrades gracefully when SPOTIFY_CLIENT_ID/SECRET aren't configured —
 * the backend returns { configured: false, message, signup_url } and we render a hint.
 */
export default function SpotifyArtistLookup({ color = '#1DB954' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [notice, setNotice] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const t = useRef(null);

  const doSearch = async (value) => {
    const term = (value ?? q).trim();
    if (!term) { setResults([]); return; }
    setSearching(true);
    try {
      const data = await spotifyArtistSearch(term);
      if (!data.configured) { setConfigured(false); setNotice(data.message); setResults([]); }
      else { setConfigured(true); setNotice(null); setResults(data.items || []); }
    } catch { setNotice('Could not reach Spotify right now — try again in a moment.'); }
    setSearching(false);
  };

  // debounce typing
  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    if (!q.trim()) { setResults([]); return; }
    t.current = setTimeout(() => doSearch(q), 400);
    return () => t.current && clearTimeout(t.current);
  // eslint-disable-next-line
  }, [q]);

  const openArtist = async (a) => {
    setLoadingDetail(true);
    setSelected({ artist: a, top_tracks: [] });
    try {
      const data = await spotifyArtistDetail(a.id);
      if (data.configured !== false) setSelected({ artist: data.artist, top_tracks: data.top_tracks || [] });
    } catch {
      setNotice('Could not load artist detail.');
    }
    setLoadingDetail(false);
  };

  const togglePreview = (track) => {
    if (!track.preview_url) return;
    const au = audioRef.current;
    if (!au) return;
    if (playingId === track.id) { au.pause(); setPlayingId(null); return; }
    au.src = track.preview_url;
    au.play().then(() => setPlayingId(track.id)).catch(() => {});
  };

  return (
    <div className="mt-3 border border-white/10 rounded-xl p-3 bg-white/[0.025]" data-testid="rc-spotify-lookup">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-5 h-5 rounded-full grid place-items-center" style={{ background: color, color: '#000' }}>
          <Music2 className="w-3 h-3" />
        </span>
        <span className="text-[12.5px] font-semibold">Live artist lookup · Spotify</span>
        <span className="chip text-[10px]" style={{ color }}>API</span>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search any artist (e.g. Radiohead, Olivia Dean)…"
          className="w-full pl-9 pr-9 py-2 bg-white/5 border border-white/10 rounded-lg text-[13px] outline-none focus:border-white/30 focus:bg-white/[0.07]"
          data-testid="rc-spotify-search-input"
        />
        {searching && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 animate-spin" />}
      </div>

      {!configured && notice && (
        <div className="mt-3 rounded-lg bg-white/[0.04] border border-white/10 p-3 flex items-start gap-2 text-[12.5px] text-white/70" data-testid="rc-spotify-not-configured">
          <Info className="w-4 h-4 text-white/50 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-white/85 mb-1">Live lookup not enabled yet</div>
            <p className="leading-relaxed">{notice}</p>
            <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noreferrer noopener" className="mt-2 inline-flex items-center gap-1 text-[12px]" style={{ color }}>
              Open Spotify Developer Dashboard <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}

      {configured && !selected && results.length > 0 && (
        <ul className="mt-3 grid gap-1.5" data-testid="rc-spotify-results">
          {results.map(a => (
            <li key={a.id}>
              <button onClick={() => openArtist(a)} className="w-full flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-transparent hover:border-white/10 text-left" data-testid={`rc-spotify-result-${a.id}`}>
                <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 bg-white/10">
                  {a.image ? <img src={a.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-white/40 text-[10px]">No art</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold truncate">{a.name}</div>
                  <div className="text-[11px] text-white/55 flex items-center gap-2 truncate">
                    <Users className="w-3 h-3" /> {Intl.NumberFormat('en', { notation: 'compact' }).format(a.followers)} followers
                    {a.genres?.[0] && <span className="truncate">· {a.genres.slice(0, 2).join(', ')}</span>}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {configured && selected && (
        <div className="mt-3" data-testid="rc-spotify-detail">
          <button onClick={() => setSelected(null)} className="text-[11.5px] text-white/55 hover:text-white mb-2">← Back to results</button>
          {loadingDetail ? (
            <div className="flex items-center gap-2 text-white/55 text-[12.5px] py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-start gap-3">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/10 shrink-0">
                  {selected.artist?.image && <img src={selected.artist.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-[18px] truncate">{selected.artist?.name}</div>
                  <div className="text-[11.5px] text-white/60 flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {Intl.NumberFormat('en', { notation: 'compact' }).format(selected.artist?.followers || 0)} followers</span>
                    {!!selected.artist?.popularity && <span className="chip text-[10px]">Popularity {selected.artist.popularity}</span>}
                  </div>
                  {selected.artist?.genres?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {selected.artist.genres.slice(0, 4).map(g => <span key={g} className="chip text-[10px]">{g}</span>)}
                    </div>
                  )}
                  {selected.artist?.spotify_url && (
                    <a href={selected.artist.spotify_url} target="_blank" rel="noreferrer noopener" className="mt-2 inline-flex items-center gap-1 text-[11.5px]" style={{ color }}>
                      Open on Spotify <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {selected.top_tracks?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wider text-white/50 font-bold mb-1.5">Top tracks</div>
                  <ul className="grid gap-1">
                    {selected.top_tracks.slice(0, 5).map(tr => (
                      <li key={tr.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-white/[0.04]">
                        <button
                          onClick={() => togglePreview(tr)}
                          disabled={!tr.preview_url}
                          title={tr.preview_url ? (playingId === tr.id ? 'Pause preview' : 'Play 30s preview') : 'No preview available'}
                          className="w-7 h-7 rounded-full grid place-items-center border border-white/15 text-white/75 hover:text-white disabled:opacity-30"
                          data-testid={`rc-spotify-preview-${tr.id}`}
                        >
                          {playingId === tr.id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="text-[12.5px] truncate">{tr.name}</div>
                          <div className="text-[10.5px] text-white/45 truncate">{tr.album}</div>
                        </div>
                        {tr.spotify_url && (
                          <a href={tr.spotify_url} target="_blank" rel="noreferrer noopener" className="text-white/45 hover:text-white" title="Open on Spotify">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {configured && !selected && q.trim() && !searching && results.length === 0 && (
        <div className="mt-3 text-[12px] text-white/50">No artists matched "{q}".</div>
      )}

      <audio ref={audioRef} onEnded={() => setPlayingId(null)} className="hidden" />
    </div>
  );
}
